import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './HomePage.css';

function Homepage({ marketData, isLoggedIn }) {
  const [stats, setStats] = useState({
    totalVolume: 0,
    activeTraders: 0,
    supportedCoins: 0
  });
  const [featuredCoins, setFeaturedCoins] = useState([]);
  const navigate = useNavigate();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
    }
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    if (isLoggedIn) {
      calculateStats();
    }
  }, [marketData, isLoggedIn]);

  // Scroll Reveal Observer
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
        }
      });
    }, { threshold: 0.1, rootMargin: "0px 0px -50px 0px" });

    const elements = document.querySelectorAll('.reveal');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, [featuredCoins]); // Re-run when coins load

  const calculateStats = () => {
    if (Object.keys(marketData).length > 0) {
      const volume = Object.values(marketData).reduce((sum, coin) => sum + (coin.volume_24h || 0), 0);
      const coinsCount = Object.keys(marketData).length;
      
      setStats({
        totalVolume: volume,
        activeTraders: 12500,
        supportedCoins: coinsCount
      });

      const topCoins = Object.values(marketData)
        .sort((a, b) => (b.market_cap || 0) - (a.market_cap || 0))
        .slice(0, 15);
      setFeaturedCoins(topCoins);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000000) {
      return (num / 1000000000).toFixed(1) + 'B';
    }
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const getCoinImage = (coin) => {
    if (coin.image) return coin.image;
    // Fallback to generic icon service if backend doesn't provide image
    const symbol = coin.symbol ? coin.symbol.replace('USDT', '').toLowerCase() : 'btc';
    return `https://assets.coincap.io/assets/icons/${symbol}@2x.png`;
  };

  // Show loading or nothing while redirecting
  if (!isLoggedIn) {
    return (
      <div className="homepage">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // Background coins for animation
  const bgCoins = ['BTC', 'ETH', 'SOL', 'XRP', 'DOGE', 'ADA'];

  return (
    <div className="homepage">
      {/* Dynamic Background */}
      <div className="homepage-bg">
        <div className="bg-orb bg-orb-1"></div>
        <div className="bg-orb bg-orb-2"></div>
        <div className="bg-orb bg-orb-3"></div>
        
        {/* Floating Background Coins */}
        <div className="floating-coins-container">
          {bgCoins.map((symbol, index) => (
            <div key={`bg-coin-${index}`} className={`floating-coin coin-${index + 1}`}>
              <img 
                src={`https://assets.coincap.io/assets/icons/${symbol.toLowerCase()}@2x.png`} 
                alt="" 
              />
            </div>
          ))}
        </div>
      </div>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1>The Future of <br /><span className="highlight">Crypto Trading</span></h1>
            <p>Experience institutional-grade trading with real-time market data, advanced charting, and lightning-fast execution. Secure, reliable, and built for pros.</p>
            <div className="hero-buttons">
              <Link to="/trade" className="btn btn-primary">
                Start Trading Now
              </Link>
              <Link to="/markets" className="btn btn-secondary">
                View Live Markets
              </Link>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-stats-grid">
              <div className="stat-card">
                <div className="stat-icon">💎</div>
                <div className="stat-content">
                  <div className="stat-value">${formatNumber(stats.totalVolume)}</div>
                  <div className="stat-label">24h Global Volume</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">👥</div>
                <div className="stat-content">
                  <div className="stat-value">{formatNumber(stats.activeTraders)}+</div>
                  <div className="stat-label">Verified Traders</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">
                  <img src="/logo.svg" alt="Market Pairs" className="stat-icon-img" style={{width: '40px', height: '40px'}} />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{stats.supportedCoins}+</div>
                  <div className="stat-label">Market Pairs</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Market Overview */}
      <section className="market-overview reveal">
        <div className="container">
          <div className="section-header">
            <h2>Market Trends</h2>
            <Link to="/markets" className="view-all">View All Markets →</Link>
          </div>
          
          <div className="market-list-container">
            <div className="market-list-header">
              <div className="list-col col-name">Asset</div>
              <div className="list-col col-price">Price</div>
              <div className="list-col col-change">24h Change</div>
              <div className="list-col col-volume">24h Volume</div>
              <div className="list-col col-chart">Last 7 Days</div>
              <div className="list-col col-action"></div>
            </div>
            
            <div className="market-list-body">
              {featuredCoins.map((coin, index) => (
                <div key={coin.symbol} className="market-list-row reveal stagger-1">
                  <div className="list-col col-name">
                    <div className="coin-icon-small">
                      <img src={getCoinImage(coin)} alt={coin.name} />
                    </div>
                    <div className="coin-identity">
                      <span className="coin-symbol">{coin.symbol}</span>
                      <span className="coin-name">{coin.name}</span>
                    </div>
                  </div>
                  
                  <div className="list-col col-price">
                    ${coin.current_price?.toLocaleString()}
                  </div>
                  
                  <div className="list-col col-change">
                    <span className={`change-badge ${coin.price_change_percentage_24h >= 0 ? 'positive' : 'negative'}`}>
                      {coin.price_change_percentage_24h >= 0 ? '+' : ''}
                      {coin.price_change_percentage_24h?.toFixed(2)}%
                    </span>
                  </div>
                  
                  <div className="list-col col-volume">
                    ${formatNumber(coin.volume_24h)}
                  </div>
                  
                  <div className="list-col col-chart">
                    <div className="mini-trend-chart">
                      <div className={`trend-line ${coin.price_change_percentage_24h >= 0 ? 'up' : 'down'}`}></div>
                    </div>
                  </div>
                  
                  <div className="list-col col-action">
                    <Link to={`/trade?pair=${coin.symbol}`} className="btn-trade-small">Trade</Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section reveal">
        <div className="container">
          <h2>Why Choose Us</h2>
          <div className="features-grid">
            <div className="feature-card reveal stagger-1">
              <div className="feature-icon">⚡</div>
              <h3>Low Latency Engine</h3>
              <p>Our matching engine handles up to 100,000 orders per second with &lt; 50ms latency.</p>
            </div>
            <div className="feature-card reveal stagger-2">
              <div className="feature-icon">🛡️</div>
              <h3>Bank-Grade Security</h3>
              <p>98% of assets stored in cold wallets. DDoS protection and full data encryption.</p>
            </div>
            <div className="feature-card reveal stagger-3">
              <div className="feature-icon">📊</div>
              <h3>Advanced Analytics</h3>
              <p>Professional charting tools with 100+ indicators for deep market analysis.</p>
            </div>
            <div className="feature-card reveal stagger-4">
              <div className="feature-icon">💳</div>
              <h3>Instant Settlements</h3>
              <p>Seamless deposits and withdrawals with automated processing systems.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="steps-section reveal">
        <div className="container">
          <h2>Start Trading in 3 Steps</h2>
          <div className="steps-grid">
            <div className="step-card reveal stagger-1">
              <div className="step-number">01</div>
              <h3>Create Account</h3>
              <p>Sign up in minutes with just your email and password.</p>
            </div>
            <div className="step-card reveal stagger-2">
              <div className="step-number">02</div>
              <h3>Deposit Funds</h3>
              <p>Fund your account via crypto or bank transfer securely.</p>
            </div>
            <div className="step-card reveal stagger-3">
              <div className="step-number">03</div>
              <h3>Start Trading</h3>
              <p>Buy, sell, and trade your favorite cryptocurrencies instantly.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile App Section */}
      <section className="app-section reveal">
        <div className="container">
          <div className="app-content">
            <div className="app-text reveal stagger-1">
              <h2>Trade on the Go</h2>
              <p>Stay connected to the markets anywhere, anytime.</p>
            </div>
            <div className="app-visual reveal stagger-2">
              <div className="phone-mockup">
                <div className="screen">
                  <div className="screen-header">TRC Pro</div>
                  <div className="screen-chart"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section reveal">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Start Your Journey?</h2>
            <p>Join thousands of traders already using Crypto Trade Pro</p>
            <div className="cta-buttons">
              <Link to="/trade" className="btn btn-primary">
                Create Free Account
              </Link>
              <Link to="/markets" className="btn btn-outline">
                Explore Markets
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="main-footer reveal">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-col">
              <h3>Crypto Trade Pro</h3>
              <p>The world's most trusted cryptocurrency exchange platform.</p>
              <div className="social-links">
                <a href="#" className="social-link">Twitter</a>
                <a href="#" className="social-link">Discord</a>
                <a href="#" className="social-link">Telegram</a>
              </div>
            </div>
            <div className="footer-col">
              <h4>Platform</h4>
              <Link to="/markets">Markets</Link>
              <Link to="/trade">Exchange</Link>
              <Link to="/trade">Earn</Link>
              <Link to="/wallet">Wallet</Link>
            </div>
            <div className="footer-col">
              <h4>Support</h4>
              <a href="#">Help Center</a>
              <a href="#">API Documentation</a>
              <a href="#">Fees</a>
              <a href="#">Security</a>
            </div>
            <div className="footer-col">
              <h4>Company</h4>
              <a href="#">About Us</a>
              <a href="#">Careers</a>
              <a href="#">Blog</a>
              <a href="#">Contact</a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 Crypto Trade Pro. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Homepage;
